// @workflow assistant.approvals
/**
 * Showing on an open task board what AVA changed (AVA-755), without a reload: which changed tasks belong to the
 * board on screen, how each is patched into the board's existing state, and where (if anywhere) to scroll.
 * The board page (views/index.vue) and the Kanban (views/Kanban.vue) apply what these decide.
 *
 * No imports, so `npm run test:unit` (node --test) can load it directly.
 */

/** How long a card AVA changed stays highlighted. */
export const CHANGED_HIGHLIGHT_MS = 4000;

const key = (value) => (value === null || value === undefined ? '' : String(value));
const sameBoard = (a, b) => key(a).toLowerCase() === key(b).toLowerCase();

/**
 * @param {Array<{ id: string, boardId: string|null }>} tasks the changed tasks the assistant announced
 * @param {string} boardId the board on screen
 * @returns {string[]} the ids to read: the board's tasks, and those whose board was not named (their read decides)
 */
export function taskIdsForBoard(tasks, boardId) {
  if (!boardId || !Array.isArray(tasks)) return [];
  return tasks.filter((task) => !task.boardId || sameBoard(task.boardId, boardId)).map((task) => key(task.id));
}

/** A filter or the search has a value: a patched task might not match it, so the list is read again instead. */
export function hasActiveFilters(options) {
  return Object.values(options || {}).some((value) =>
    Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== ''
  );
}

/**
 * @param {{ boardId: string, current: object[], fetched: object[], filtered?: boolean }} input
 *   current: the board's tasks (store); fetched: the changed tasks as just read from the API
 * @returns {{ update: Array<{ task: object, moved: boolean }>, add: object[], refetch: boolean, ids: string[] }}
 *   update: tasks on the board, patched in place (moved: the status changed); add: new tasks on the board;
 *   refetch: read the list again (quietly) instead, when a patch could be wrong; ids: the board's changed tasks
 */
export function planTaskPatch({ boardId, current, fetched, filtered = false }) {
  const known = new Map((current || []).map((task) => [key(task.id), task]));
  const onBoard = (fetched || []).filter((task) => sameBoard(task.typeId, boardId));
  const leftBoard = (fetched || []).some((task) => !sameBoard(task.typeId, boardId) && known.has(key(task.id)));
  const ids = onBoard.map((task) => key(task.id));
  if (leftBoard || (filtered && onBoard.length > 0)) return { update: [], add: [], refetch: true, ids };

  const update = [];
  const add = [];
  for (const task of onBoard) {
    const before = known.get(key(task.id));
    if (before) update.push({ task, moved: key(before.statusId) !== key(task.statusId) });
    else add.push(task);
  }
  return { update, add, refetch: false, ids };
}

/**
 * @param {string[]} ids the changed cards, in order
 * @param {string[]} visibleIds those of them on screen
 * @returns {string|null} the card to scroll to: one card that is off screen, or the first of several when none is
 *   on screen; otherwise null (the board does not jump around)
 */
export function scrollTargetFor(ids, visibleIds) {
  if (!ids || ids.length === 0) return null;
  const visible = new Set((visibleIds || []).map(key));
  if (ids.length === 1) return visible.has(key(ids[0])) ? null : ids[0];
  return ids.some((id) => visible.has(key(id))) ? null : ids[0];
}

/**
 * The cards highlighted after another change: each AVA write names one task, so several changes arrive one by one.
 * A change during the highlight joins the cards already shown (order kept, no repeats), and the scroll rule then
 * looks at all of them, so the board does not jump from card to card.
 *
 * @param {string[]} shown the cards still highlighted ([] when none)
 * @param {Array<string|number>} incoming the cards just changed
 * @returns {string[]}
 */
export function mergeChangedIds(shown, incoming) {
  return [...new Set([...(shown || []), ...(incoming || [])].map(key))];
}
